import { useRef } from "react";
import { motion, useScroll, useReducedMotion } from "framer-motion";
import { SectionLabel, Reveal } from "../Reveal";

const STEPS = [
  {
    n: "01",
    title: "Tell Us About Your Business",
    copy: "Share a little about your company and what you'd like to explore.",
    viz: (
      <svg viewBox="0 0 120 80" className="w-28 h-20" role="img" aria-label="Chat bubbles">
        <rect x="6" y="10" width="64" height="30" rx="10" fill="#FFFFFF" stroke="#234D85" strokeWidth="1.6" />
        <line x1="16" y1="21" x2="56" y2="21" stroke="#8FB3D9" strokeWidth="2.4" strokeLinecap="round" />
        <line x1="16" y1="29" x2="44" y2="29" stroke="#C9DBEA" strokeWidth="2.4" strokeLinecap="round" />
        <rect x="52" y="44" width="62" height="28" rx="10" fill="#15B8A6" opacity="0.92" />
        <line x1="62" y1="58" x2="102" y2="58" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" opacity="0.85" />
      </svg>
    ),
  },
  {
    n: "02",
    title: "We Identify Relevant Resources",
    copy: "We help you connect with specialists who focus on the areas that matter to you.",
    viz: (
      <svg viewBox="0 0 120 80" className="w-28 h-20" role="img" aria-label="Magnifier over options">
        <rect x="8" y="12" width="30" height="22" rx="6" fill="#EAF4F8" stroke="#234D85" strokeWidth="1.4" />
        <rect x="8" y="40" width="30" height="22" rx="6" fill="#EAF4F8" stroke="#234D85" strokeWidth="1.4" />
        <rect x="44" y="26" width="30" height="22" rx="6" fill="#D7F2EE" stroke="#15B8A6" strokeWidth="1.6" />
        <circle cx="84" cy="38" r="18" fill="none" stroke="#101F38" strokeWidth="2.4" />
        <line x1="97" y1="52" x2="110" y2="66" stroke="#101F38" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="84" cy="38" r="7" fill="#15B8A6" opacity="0.5" />
      </svg>
    ),
  },
  {
    n: "03",
    title: "Explore Your Options",
    copy: "Specialists can help evaluate your circumstances and explain available options, potential savings, eligibility, and costs.",
    viz: (
      <svg viewBox="0 0 120 80" className="w-28 h-20" role="img" aria-label="Fan of option cards">
        <rect x="18" y="18" width="34" height="46" rx="7" fill="#FFFFFF" stroke="#234D85" strokeWidth="1.5" transform="rotate(-8 35 41)" />
        <rect x="43" y="14" width="34" height="46" rx="7" fill="#FFFFFF" stroke="#15B8A6" strokeWidth="1.8" />
        <rect x="51" y="24" width="18" height="5" rx="2.5" fill="#15B8A6" />
        <rect x="68" y="18" width="34" height="46" rx="7" fill="#FFFFFF" stroke="#234D85" strokeWidth="1.5" transform="rotate(8 85 41)" />
      </svg>
    ),
  },
  {
    n: "04",
    title: "You Decide",
    copy: "You determine whether any recommendation makes sense for your business.",
    viz: (
      <svg viewBox="0 0 120 80" className="w-28 h-20" role="img" aria-label="Shield with checkmark">
        <path d="M60 8 L88 18 L88 44 C 88 60, 75 70, 60 76 C 45 70, 32 60, 32 44 L32 18 Z" fill="#EAF4F8" stroke="#101F38" strokeWidth="2" />
        <path d="M48 42 L57 51 L74 32" stroke="#15B8A6" strokeWidth="3.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

function Step({ step, i, vertical }) {
  return (
    <motion.div
      className={vertical ? "relative pl-14 pb-12 last:pb-0" : "relative flex-1"}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6, delay: i * 0.05 }}
      data-testid={`process-step-${i + 1}`}
    >
      {vertical && (
        <span className="absolute left-0 top-0 flex items-center justify-center w-9 h-9 rounded-full bg-white border-2 border-teal text-[12px] font-extrabold text-navy shadow-card">
          {step.n}
        </span>
      )}
      {!vertical && (
        <span className="relative z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white border-2 border-teal text-[13px] font-extrabold text-navy shadow-card">
          {step.n}
        </span>
      )}
      <div className={vertical ? "" : "mt-6 pr-6"}>
        <div className="mb-3">{step.viz}</div>
        <h3 className="font-display font-extrabold tracking-tight text-navy text-lg">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate">{step.copy}</p>
      </div>
    </motion.div>
  );
}

export default function ProcessTimeline() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.6"] });

  return (
    <section id="how-it-works" ref={ref} className="relative py-16 md:py-24 bg-white" data-testid="process-timeline">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <SectionLabel>How It Works</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              A Simpler Way to Find Better Opportunities
            </h2>
          </Reveal>
        </div>

        {/* Desktop horizontal */}
        <div className="hidden md:block mt-16 relative">
          <div className="absolute top-[22px] left-0 right-0 h-[2px] bg-ice-deep" aria-hidden="true">
            <motion.div
              className="h-full bg-gradient-to-r from-royal to-teal origin-left"
              style={{ scaleX: reduced ? 1 : scrollYProgress }}
            />
          </div>
          <div className="relative flex">
            {STEPS.map((s, i) => (
              <Step key={s.n} step={s} i={i} vertical={false} />
            ))}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="md:hidden mt-12 relative">
          <div className="absolute left-[17px] top-0 bottom-0 w-[2px] bg-ice-deep" aria-hidden="true">
            <motion.div className="w-full h-full bg-gradient-to-b from-royal to-teal origin-top" style={{ scaleY: reduced ? 1 : scrollYProgress }} />
          </div>
          {STEPS.map((s, i) => (
            <Step key={s.n} step={s} i={i} vertical />
          ))}
        </div>
      </div>
    </section>
  );
}
